import { supabase } from "../supabase";
import { formatSupabaseError } from "./estimates";
import {
  applyOrganizationScopeForActor,
  resolveOrganizationIdForQueries,
  stampOrganizationIds,
} from "./orgQueryScope";
import { scopeOperationalTimeLogsForActor } from "./scopedQueries";
import {
  requireMaterialUsageVoid,
  requireProjectOperationalMaterialCommit,
} from "./financialGuards";
import { CAPABILITY } from "./permissions";
import { isUserIdInCapabilityScope } from "./scopedQueries";
import { getMaterialUsageRelationName, getTimeLogsRelationName } from "./operationalProjections";
import { isUuidString, requireUuid } from "./uuidFields";
import { safeUserErrorMessage } from "./safeErrors";
import { devLog } from "./devLog";

export const MATERIAL_USAGE_SOURCE = Object.freeze({
  FINALIZE_SESSION: "finalize_session",
  SESSION_INCREMENTAL: "session_incremental",
  MANUAL_ENTRY: "manual_entry",
  PROJECT_DETAIL: "project_detail",
  TIME_LOGS_HUB: "time_logs_hub",
  ABANDONED_RECOVERY: "abandoned_recovery",
});

export const MATERIAL_COMMIT_RPC = "cp_commit_material_usage_batch";
export const MATERIAL_ASSOCIATE_RPC = "cp_associate_session_material_time_log";
export const MATERIAL_QUANTITY_RPC = "cp_update_material_usage_quantity";

const MATERIAL_COMMIT_USER_ERROR =
  "Materials could not be saved. Please retry.";

function wrapSupabaseError(error) {
  const wrapped = new Error(formatSupabaseError(error));
  wrapped.cause = error;
  throw wrapped;
}

function isUniqueViolation(error) {
  return error?.code === "23505";
}

/** Persisted usage cost. Blank stays 0. Non-finite garbage stays 0. Finite values, including 0, are kept. */
export function finiteUnitCostSnapshot(value) {
  if (value == null || value === "") return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function mapMaterialCommitUserError(
  error,
  fallback = MATERIAL_COMMIT_USER_ERROR,
) {
  return new Error(safeUserErrorMessage(error, fallback));
}

function logMaterialCommitFailure(scope, details = {}) {
  const { error, ...rest } = details;
  devLog(scope, {
    ...rest,
    errorName: error?.name || null,
    errorCode: error?.code || error?.cause?.code || null,
  });
}

function parseTimeLogId(timeLogId) {
  if (timeLogId == null || timeLogId === "") {
    return null;
  }
  const parsed = Number(timeLogId);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new Error("time_log_id is invalid.");
  }
  return parsed;
}

function buildRpcLine(line) {
  const materialId = requireUuid(line?.material_id, "material_id");
  const quantity = Number(line?.quantity);
  if (!quantity || quantity <= 0) {
    throw new Error("Quantity must be greater than zero.");
  }

  const name = String(line?.material_name_snapshot || "").trim();
  if (!name) {
    throw new Error("material_name_snapshot is required.");
  }

  return {
    client_line_id: line?.localId || line?.client_line_id || null,
    material_id: materialId,
    quantity,
    notes: line?.notes?.trim() || null,
    material_name_snapshot: name,
    sku_snapshot: line?.sku_snapshot || null,
    unit_of_measure_snapshot: line?.unit_of_measure_snapshot || null,
  };
}

function mapRpcCommitLines(payload) {
  return (payload?.lines || []).map((row) => ({
    clientLineId: row.client_line_id || null,
    usageId: row.usage_id || null,
    materialId: row.material_id || null,
    quantity: row.quantity ?? null,
    timeLogId: row.time_log_id ?? null,
    commitBatchId: row.commit_batch_id || payload.commit_batch_id || null,
  }));
}

export function sumMaterialCost(rows = []) {
  return rows.reduce((sum, row) => sum + finiteUnitCostSnapshot(row.total_cost), 0);
}

function isStableMaterialId(materialId) {
  if (materialId == null) return false;
  const text = String(materialId).trim();
  if (!text) return false;
  return !["null", "undefined", "none"].includes(text.toLowerCase());
}

export function materialUsageDisplayGroupKey(row) {
  if (isStableMaterialId(row?.material_id)) {
    return `material:${String(row.material_id).trim()}`;
  }
  return `usage:${row?.usage_id || ""}`;
}

/**
 * Project Detail presentation only. Groups live usage rows by stable
 * material_id. Does not mutate, collapse, or rewrite ledger rows.
 * Null/blank material_id rows stay unmerged.
 */
export function groupProjectMaterialUsageForDisplay(rows = []) {
  const groups = [];
  const byKey = new Map();

  for (const row of rows || []) {
    if (row?.voided_at) continue;

    const groupKey = materialUsageDisplayGroupKey(row);
    let group = byKey.get(groupKey);
    if (!group) {
      group = {
        groupKey,
        material_id: isStableMaterialId(row.material_id) ? row.material_id : null,
        material_name_snapshot: row.material_name_snapshot || "",
        sku_snapshot: row.sku_snapshot || null,
        unit_of_measure_snapshot: row.unit_of_measure_snapshot || null,
        quantity: 0,
        total_cost: 0,
        rows: [],
      };
      byKey.set(groupKey, group);
      groups.push(group);
    }

    group.rows.push(row);
    group.quantity += Number(row.quantity || 0);
    group.total_cost += Number(row.total_cost || 0);
  }

  return groups;
}

export function mapUsageRowsWithTimelogDates(rows = [], dateByTimelogId = new Map()) {
  const lookup =
    dateByTimelogId instanceof Map
      ? dateByTimelogId
      : new Map(
          Object.entries(dateByTimelogId).map(([id, dateIso]) => [
            Number(id),
            dateIso,
          ]),
        );

  return (rows || []).map((row) => ({
    ...row,
    time_log_date_iso: row.time_log_id
      ? lookup.get(row.time_log_id) || null
      : null,
  }));
}

export async function attachTimelogEntryDates(rows = [], actor = null) {
  const timeLogIds = [
    ...new Set((rows || []).map((row) => row.time_log_id).filter(Boolean)),
  ];

  if (!timeLogIds.length) {
    return mapUsageRowsWithTimelogDates(rows);
  }

  let query = supabase
    .from(actor ? getTimeLogsRelationName(actor) : "time_logs")
    .select("id, date_iso")
    .in("id", timeLogIds);

  if (actor) {
    query = await scopeOperationalTimeLogsForActor(query, supabase, actor);
  }

  const { data, error } = await query;

  if (error) wrapSupabaseError(error);

  const dateById = new Map((data || []).map((log) => [log.id, log.date_iso]));
  return mapUsageRowsWithTimelogDates(rows, dateById);
}

export async function loadMaterialUsageForProject(
  projectId,
  { includeVoided = false, actor = null, orgPolicies = null } = {},
) {
  if (!projectId) return [];

  const relation = getMaterialUsageRelationName(actor, orgPolicies);
  let query = supabase
    .from(relation)
    .select("*")
    .eq("project_id", projectId)
    .order("usage_date", { ascending: false })
    .order("created_at", { ascending: false });

  if (!includeVoided) {
    query = query.is("voided_at", null);
  }

  if (actor) {
    query = await applyOrganizationScopeForActor(query, actor, supabase, {
      tableName: "project_material_usage",
    });
  }

  const { data, error } = await query;
  if (error) wrapSupabaseError(error);
  return data || [];
}

export async function loadMaterialUsageForSession(
  sessionId,
  { includeVoided = false, actor = null, orgPolicies = null } = {},
) {
  if (!sessionId) return [];

  const relation = getMaterialUsageRelationName(actor, orgPolicies);
  let query = supabase
    .from(relation)
    .select("*")
    .eq("session_id", sessionId)
    .order("created_at", { ascending: true });

  if (!includeVoided) {
    query = query.is("voided_at", null);
  }

  if (actor) {
    query = await applyOrganizationScopeForActor(query, actor, supabase, {
      tableName: "project_material_usage",
    });
  }

  const { data, error } = await query;
  if (error) wrapSupabaseError(error);
  return data || [];
}

/**
 * Load material usage lines for one time log (session materials for that work entry).
 * Prefer `time_log_id`; when `sessionId` is provided, also include active session
 * rows not yet stamped with `time_log_id` (pre-finalize / orphan incremental).
 */
export async function loadMaterialUsageForTimeLog(
  timeLogId,
  { sessionId = null, includeVoided = false, actor = null, orgPolicies = null } = {},
) {
  if (!timeLogId && !sessionId) return [];

  const byId = new Map();

  if (timeLogId) {
    const relation = getMaterialUsageRelationName(actor, orgPolicies);
    let query = supabase
      .from(relation)
      .select("*")
      .eq("time_log_id", timeLogId)
      .order("created_at", { ascending: true });

    if (!includeVoided) {
      query = query.is("voided_at", null);
    }

    if (actor) {
      query = await applyOrganizationScopeForActor(query, actor, supabase, {
        tableName: "project_material_usage",
      });
    }

    const { data, error } = await query;
    if (error) wrapSupabaseError(error);
    for (const row of data || []) {
      byId.set(row.usage_id, row);
    }
  }

  if (sessionId) {
    const sessionRows = await loadMaterialUsageForSession(sessionId, {
      includeVoided,
      actor,
    });
    for (const row of sessionRows) {
      if (row.time_log_id && timeLogId && Number(row.time_log_id) !== Number(timeLogId)) {
        continue;
      }
      if (!byId.has(row.usage_id)) {
        byId.set(row.usage_id, row);
      }
    }
  }

  return [...byId.values()].sort((a, b) =>
    String(a.created_at || "").localeCompare(String(b.created_at || "")),
  );
}

/**
 * Materials Used on a time log inherit TIMELOG_EDIT scope for that log's employee.
 * Does not use material.usage.manual / material.usage.void.
 */
export function canEditTimeLogMaterials(actor, timeLog, context = {}) {
  if (!actor || !timeLog) return false;

  const ownerId = timeLog.supabase_user_id || timeLog.user_id || null;
  return isUserIdInCapabilityScope(
    actor,
    ownerId,
    CAPABILITY.TIMELOG_EDIT,
    context,
  );
}

function requireTimeLogMaterialsEdit(actor, timeLog, context = {}) {
  if (!canEditTimeLogMaterials(actor, timeLog, context)) {
    throw new Error("Not authorized to modify materials for this time log.");
  }
}

async function softVoidMaterialUsage(usageId, voidedBy) {
  if (!usageId) {
    throw new Error("usageId is required to void material usage.");
  }

  const { data, error } = await supabase
    .from("project_material_usage")
    .update({
      voided_at: new Date().toISOString(),
      voided_by: voidedBy ?? null,
    })
    .eq("usage_id", usageId)
    .is("voided_at", null)
    .select("usage_id")
    .maybeSingle();

  if (error) wrapSupabaseError(error);
  if (!data) {
    throw new Error("Material usage not found or already voided.");
  }

  return data;
}

/**
 * Quantity / notes correction through a definer RPC.
 * The base table SELECT is manager+ ledger only, so an employee update
 * cannot read unit_cost_snapshot or total_cost back.
 */
async function updateMaterialUsageQuantity({ usageId, quantity, notes = undefined }) {
  if (!usageId) {
    throw new Error("usageId is required to update material usage.");
  }

  const parsedQuantity = Number(quantity);
  if (!parsedQuantity || parsedQuantity <= 0) {
    throw new Error("Quantity must be greater than zero.");
  }

  const updateNotes = notes !== undefined;
  const { data, error } = await supabase.rpc(MATERIAL_QUANTITY_RPC, {
    p_usage_id: usageId,
    p_quantity: parsedQuantity,
    p_notes: updateNotes
      ? typeof notes === "string"
        ? notes.trim() || null
        : null
      : null,
    p_update_notes: updateNotes,
  });

  if (error) wrapSupabaseError(error);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row?.usage_id) {
    throw new Error("Material usage not found or already voided.");
  }

  return row;
}

/**
 * Operational correction of a committed usage row. The caller must own the
 * row or be manager+. Does not read financial columns.
 */
export async function updateCommittedMaterialUsage({
  usageId,
  quantity,
  notes = undefined,
}) {
  return updateMaterialUsageQuantity({ usageId, quantity, notes });
}

export async function voidCommittedMaterialUsage({ usageId, voidedBy = null }) {
  return softVoidMaterialUsage(usageId, voidedBy);
}

export async function sumMaterialCostForProject(projectId) {
  const rows = await loadMaterialUsageForProject(projectId);
  return sumMaterialCost(rows);
}

export async function getCommitBatchByIdempotencyKey(idempotencyKey) {
  if (!idempotencyKey) return null;

  const { data, error } = await supabase
    .from("project_material_commit_batches")
    .select("*")
    .eq("idempotency_key", idempotencyKey)
    .maybeSingle();

  if (error) wrapSupabaseError(error);
  return data;
}

export async function loadUsageLinesForBatch(commitBatchId) {
  if (!commitBatchId) return [];

  const { data, error } = await supabase
    .from("project_material_usage")
    .select("*")
    .eq("commit_batch_id", commitBatchId)
    .is("voided_at", null);

  if (error) wrapSupabaseError(error);
  return data || [];
}

/**
 * Create a commit batch. Returns existing batch on idempotency_key conflict.
 */
export async function createCommitBatch({
  idempotencyKey,
  sessionId,
  projectId,
  timeLogId = null,
  committedBy,
  source = null,
}) {
  if (!idempotencyKey) {
    throw new Error("idempotencyKey is required for material commit batches.");
  }
  if (!sessionId) {
    throw new Error("sessionId is required for material commit batches.");
  }
  if (!projectId || !committedBy) {
    throw new Error("projectId and committedBy are required for commit batches.");
  }

  const { data, error } = await supabase
    .from("project_material_commit_batches")
    .insert({
      idempotency_key: idempotencyKey,
      session_id: sessionId,
      project_id: projectId,
      time_log_id: timeLogId,
      committed_by: committedBy,
      source,
    })
    .select("commit_batch_id")
    .single();

  if (!error) {
    return { commitBatchId: data.commit_batch_id, created: true };
  }

  if (isUniqueViolation(error)) {
    const existing = await getCommitBatchByIdempotencyKey(idempotencyKey);
    if (!existing) wrapSupabaseError(error);
    return { commitBatchId: existing.commit_batch_id, created: false };
  }

  wrapSupabaseError(error);
  return null;
}

export async function insertUsageLines(commitBatchId, lines, actor = null) {
  if (!commitBatchId) {
    throw new Error("commitBatchId is required to insert usage lines.");
  }
  if (!lines?.length) {
    return { inserted: 0 };
  }

  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);

  const payload = stampOrganizationIds(
    lines.map((line) => ({
      ...line,
      commit_batch_id: commitBatchId,
    })),
    organizationId,
  );

  const { error } = await supabase.from("project_material_usage").insert(payload);
  if (error) wrapSupabaseError(error);
  return { inserted: payload.length };
}

export async function resolveServerCostSnapshots(lines, actor = null) {
  const materialIds = [
    ...new Set(lines.map((line) => line.material_id).filter(Boolean)),
  ];

  if (!materialIds.length) {
    return lines.map((line) => ({
      ...line,
      unit_cost_snapshot: finiteUnitCostSnapshot(line.unit_cost_snapshot),
    }));
  }

  let query = supabase
    .from("materials")
    .select("material_id, unit_cost")
    .in("material_id", materialIds);

  if (actor) {
    query = await applyOrganizationScopeForActor(query, actor, supabase, {
      tableName: "materials",
    });
  }

  const { data, error } = await query;

  if (error) wrapSupabaseError(error);

  const costById = new Map(
    (data || []).map((row) => [row.material_id, finiteUnitCostSnapshot(row.unit_cost)]),
  );

  return lines.map((line) => ({
    ...line,
    unit_cost_snapshot: costById.has(line.material_id)
      ? costById.get(line.material_id)
      : finiteUnitCostSnapshot(line.unit_cost_snapshot),
  }));
}

function buildUsageLinePayload(line, {
  projectId,
  enteredBy,
  sessionId,
  timeLogId,
  usageDate,
  source,
}) {
  return {
    project_id: projectId,
    material_id: line.material_id,
    quantity: line.quantity,
    unit_cost_snapshot: line.unit_cost_snapshot,
    material_name_snapshot: line.material_name_snapshot,
    sku_snapshot: line.sku_snapshot || null,
    unit_of_measure_snapshot: line.unit_of_measure_snapshot || null,
    entered_by: enteredBy,
    usage_date: line.usage_date || usageDate,
    notes: line.notes?.trim() || null,
    session_id: sessionId || null,
    time_log_id: timeLogId || null,
    source,
    quote_line_snapshot_id: line.quote_line_snapshot_id || null,
  };
}

/**
 * Commit material usage through the atomic authenticated RPC.
 * Organization is stamped from the project on the server before insert.
 * Catalog pricing and quote-snapshot reads are not client hard dependencies.
 */
export async function commitMaterialUsageBatch({
  idempotencyKey,
  sessionId,
  projectId,
  enteredBy,
  timeLogId = null,
  usageDate,
  lines,
  source = MATERIAL_USAGE_SOURCE.FINALIZE_SESSION,
}) {
  if (!lines?.length) {
    return { error: null, skipped: true, commitBatchId: null, lines: [] };
  }

  let parsedTimeLogId = null;
  let rpcLines;

  try {
    if (!isUuidString(idempotencyKey)) {
      throw new Error("idempotencyKey is required.");
    }
    if (
      !sessionId ||
      String(sessionId).trim() === "" ||
      String(sessionId).trim().toLowerCase() === "none"
    ) {
      throw new Error("sessionId is required.");
    }
    requireUuid(projectId, "projectId");
    requireUuid(enteredBy, "enteredBy");
    parsedTimeLogId = parseTimeLogId(timeLogId);
    rpcLines = lines.map(buildRpcLine);
  } catch (error) {
    return {
      error: mapMaterialCommitUserError(error),
      skipped: false,
      commitBatchId: null,
      lines: [],
    };
  }

  const { data, error } = await supabase.rpc(MATERIAL_COMMIT_RPC, {
    p_idempotency_key: idempotencyKey,
    p_session_id: String(sessionId).trim(),
    p_project_id: projectId,
    p_time_log_id: parsedTimeLogId,
    p_usage_date: usageDate || null,
    p_source: source || MATERIAL_USAGE_SOURCE.MANUAL_ENTRY,
    p_entered_by: enteredBy,
    p_lines: rpcLines,
  });

  if (error) {
    logMaterialCommitFailure("MATERIAL_COMMIT_RPC_ERROR", {
      sessionId,
      projectId,
      source,
      error,
    });
    return {
      error: mapMaterialCommitUserError(error),
      skipped: false,
      commitBatchId: data?.commit_batch_id || null,
      lines: mapRpcCommitLines(data),
    };
  }

  const commitBatchId = data?.commit_batch_id || null;
  const mappedLines = mapRpcCommitLines(data);
  const hasUsageRows =
    mappedLines.length > 0 && mappedLines.every((row) => row.usageId);

  if (!commitBatchId || !hasUsageRows) {
    logMaterialCommitFailure("MATERIAL_COMMIT_RPC_INCOMPLETE", {
      sessionId,
      projectId,
      source,
      commitBatchId,
      lineCount: mappedLines.length,
    });
    return {
      error: new Error(MATERIAL_COMMIT_USER_ERROR),
      skipped: false,
      commitBatchId,
      lines: mappedLines,
    };
  }

  return {
    error: null,
    skipped: Boolean(data?.idempotent),
    commitBatchId,
    organizationId: data?.organization_id || null,
    lines: mappedLines,
  };
}

export async function associateSessionMaterialTimeLog({
  sessionId,
  timeLogId,
}) {
  try {
    if (!sessionId || String(sessionId).trim() === "") {
      throw new Error("sessionId is required.");
    }
    const parsedTimeLogId = parseTimeLogId(timeLogId);
    if (parsedTimeLogId == null) {
      throw new Error("time_log_id is required.");
    }

    const { data, error } = await supabase.rpc(MATERIAL_ASSOCIATE_RPC, {
      p_session_id: String(sessionId).trim(),
      p_time_log_id: parsedTimeLogId,
    });

    if (error) {
      logMaterialCommitFailure("MATERIAL_ASSOCIATE_RPC_ERROR", {
        sessionId,
        timeLogId: parsedTimeLogId,
        error,
      });
      return {
        error: mapMaterialCommitUserError(error),
        updatedUsage: 0,
        updatedBatches: 0,
        remainingUnstamped: null,
      };
    }

    const remainingUnstamped = Number(data?.remaining_unstamped || 0);
    if (remainingUnstamped > 0) {
      logMaterialCommitFailure("MATERIAL_ASSOCIATE_RPC_INCOMPLETE", {
        sessionId,
        timeLogId: parsedTimeLogId,
        remainingUnstamped,
      });
      return {
        error: new Error(MATERIAL_COMMIT_USER_ERROR),
        updatedUsage: Number(data?.updated_usage || 0),
        updatedBatches: Number(data?.updated_batches || 0),
        remainingUnstamped,
        organizationId: data?.organization_id || null,
      };
    }

    return {
      error: null,
      updatedUsage: Number(data?.updated_usage || 0),
      updatedBatches: Number(data?.updated_batches || 0),
      remainingUnstamped,
      alreadyStamped: Number(data?.already_stamped || 0),
      organizationId: data?.organization_id || null,
    };
  } catch (error) {
    return {
      error: mapMaterialCommitUserError(error),
      updatedUsage: 0,
      updatedBatches: 0,
      remainingUnstamped: null,
    };
  }
}

/**
 * Manual material entry from Project Detail — one batch per save action.
 */
export async function insertManualMaterialUsage({
  actor,
  projectId,
  material,
  quantity,
  enteredBy,
  usageDate,
  notes = null,
  source = MATERIAL_USAGE_SOURCE.PROJECT_DETAIL,
  idempotencyKey = null,
}) {
  requireProjectOperationalMaterialCommit(actor);

  if (!projectId || !material?.material_id || !enteredBy) {
    throw new Error("projectId, material, and enteredBy are required.");
  }

  const parsedQuantity = Number(quantity);
  if (!parsedQuantity || parsedQuantity <= 0) {
    throw new Error("Quantity must be greater than zero.");
  }

  const resolvedKey = isUuidString(idempotencyKey) ? idempotencyKey : crypto.randomUUID();
  idempotencyKey = resolvedKey;
  const sessionId = `manual_entry:${idempotencyKey}`;

  const draftLine = {
    material_id: material.material_id,
    quantity: parsedQuantity,
    material_name_snapshot: material.material_name,
    sku_snapshot: material.sku || null,
    unit_of_measure_snapshot: material.unit_of_measure || null,
    unit_cost_snapshot: finiteUnitCostSnapshot(material.unit_cost),
    usage_date: usageDate,
    notes,
  };

  const result = await commitMaterialUsageBatch({
    idempotencyKey,
    sessionId,
    projectId,
    enteredBy,
    usageDate,
    lines: [draftLine],
    source,
  });

  if (result.error) {
    throw result.error;
  }

  return result;
}

export async function voidMaterialUsage(actor, usageId, voidedBy) {
  requireMaterialUsageVoid(actor);
  return softVoidMaterialUsage(usageId, voidedBy);
}

/**
 * Soft-void a usage line for a time log. Authorization follows TIMELOG_EDIT on the
 * parent log — not material.usage.void (Project Detail manager correction path).
 */
export async function voidTimeLogMaterialUsage({
  actor,
  timeLog,
  usageId,
  voidedBy = null,
  directReportIds,
}) {
  requireTimeLogMaterialsEdit(actor, timeLog, { directReportIds });
  return softVoidMaterialUsage(
    usageId,
    voidedBy ?? actor?.supabase_user_id ?? actor?.user_id ?? null,
  );
}

/**
 * Update quantity / notes on a time-log material line (operational correction).
 * Authorization inherits TIMELOG_EDIT from the parent time log.
 */
export async function updateTimeLogMaterialUsage({
  actor,
  timeLog,
  usageId,
  quantity,
  notes = undefined,
  directReportIds,
}) {
  requireTimeLogMaterialsEdit(actor, timeLog, { directReportIds });
  return updateMaterialUsageQuantity({ usageId, quantity, notes });
}

/**
 * Add one catalog material line to an existing time log (ledger write via commit batch).
 */
export async function commitTimeLogMaterialUsage({
  actor,
  timeLog,
  material,
  quantity,
  notes = null,
  directReportIds,
}) {
  requireTimeLogMaterialsEdit(actor, timeLog, { directReportIds });

  if (!timeLog?.id) {
    throw new Error("timeLog.id is required to commit material usage.");
  }
  if (!timeLog.project_id) {
    throw new Error("A project is required before recording materials.");
  }
  if (!material?.material_id) {
    throw new Error("material is required.");
  }

  const sessionId =
    timeLog.session_id || `MANUAL-TL-${timeLog.id}`;
  const enteredBy =
    timeLog.supabase_user_id ||
    timeLog.user_id ||
    actor?.supabase_user_id ||
    actor?.user_id;

  if (!enteredBy) {
    throw new Error("Cannot resolve entered_by for material usage.");
  }

  const parsedQuantity = Number(quantity);
  if (!parsedQuantity || parsedQuantity <= 0) {
    throw new Error("Quantity must be greater than zero.");
  }

  const usageDate =
    timeLog.date_iso || new Date().toISOString().split("T")[0];
  const localId = crypto.randomUUID();
  const draftLine = {
    localId,
    material_id: material.material_id,
    quantity: parsedQuantity,
    material_name_snapshot: material.material_name,
    sku_snapshot: material.sku || null,
    unit_of_measure_snapshot: material.unit_of_measure || null,
    unit_cost_snapshot: finiteUnitCostSnapshot(material.unit_cost),
    usage_date: usageDate,
    notes: notes?.trim() || null,
  };

  const result = await commitMaterialUsageBatch({
    idempotencyKey: localId,
    sessionId,
    projectId: timeLog.project_id,
    enteredBy,
    timeLogId: timeLog.id,
    usageDate,
    lines: [draftLine],
    source: MATERIAL_USAGE_SOURCE.TIME_LOGS_HUB,
  });

  if (result.error) {
    throw result.error;
  }

  return result;
}
